/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import type { Credentials } from '@opengeoweb/api';
import type { ComponentsLookUpPayload as CapComponentsLookUpPayload } from '@opengeoweb/cap';
import { ComponentsLookUp as capComponentsLookup } from '@opengeoweb/cap';
import type { ComponentsLookUpPayload as CoreComponentsLookUpPayload } from '@opengeoweb/core';
import { componentsLookUp as coreComponentsLookUp } from '@opengeoweb/core';
import type { ComponentsLookUpPayload as TrajksComponentsLookUpPayload } from '@opengeoweb/knmi-components';
import { componentsLookUp as trajksComponentsLookUp } from '@opengeoweb/knmi-components';
import { componentsLookUp as pluginComponentsLookUp } from '@opengeoweb/plugins';
import type { ComponentsLookUpPayload as PluginComponentsLookUpPayload } from '@opengeoweb/plugins';
import type { ConfigType } from '@opengeoweb/shared';
import type {
  SigmetComponentsLookUpPayload,
  AirmetComponentsLookUpPayload,
} from '@opengeoweb/sigmet-airmet';
import {
  SigmetComponentsLookUp,
  AirmetComponentsLookUp,
} from '@opengeoweb/sigmet-airmet';
import type { SoundingsLookUpPayload } from '@opengeoweb/soundings';
import { soundingsLookUp } from '@opengeoweb/soundings';
import type { ComponentsLookUpPayload as SpaceweatherComponentsLookUpPayload } from '@opengeoweb/space-weather';
import { componentsLookUp as spaceweatherComponentsLookUp } from '@opengeoweb/space-weather';
import type { ComponentsLookUpPayload as TafComponentsLookUpPayload } from '@opengeoweb/taf';
import { componentsLookUp as tafComponentsLookUp } from '@opengeoweb/taf';
import type { ComponentsLookUpPayload as TimeseriesComponentsLookUpPayload } from '@opengeoweb/time-series';
import { componentsLookUp as timeseriesComponentsLookUp } from '@opengeoweb/time-series';
import type { ComponentsLookUpPayload as WarningListComponentsLookUpPayLoad } from '@opengeoweb/warnings';
import {
  ObjectManagerMapButtonConnect,
  DrawingToolMapButtonConnect,
  componentsLookUp as warningListComponentsLookUp,
} from '@opengeoweb/warnings';
import { ComponentsLookUpWrapper } from '@opengeoweb/workspace';

export type ComponentsLookUpPayload =
  | CoreComponentsLookUpPayload
  | TimeseriesComponentsLookUpPayload
  | TafComponentsLookUpPayload
  | CapComponentsLookUpPayload
  | SigmetComponentsLookUpPayload
  | AirmetComponentsLookUpPayload
  | WarningListComponentsLookUpPayLoad
  | SpaceweatherComponentsLookUpPayload
  | SoundingsLookUpPayload
  | TrajksComponentsLookUpPayload
  | PluginComponentsLookUpPayload;

/**
 * The lookup table is for registering your own components with the workspace.
 * @param ComponentsLookUpPayload payload
 * @returns each respective component
 */
export const componentsLookUp = (
  payload: ComponentsLookUpPayload,
  config?: ConfigType,
  auth?: Credentials,
  onSetAuth?: (credentias: Credentials) => void,
): React.ReactElement => {
  switch (payload.componentType) {
    case 'TimeSeries':
      return timeseriesComponentsLookUp(payload);
    case 'Plugin':
      return pluginComponentsLookUp({ ...payload, applicationConfig: config });
    case 'TafModule':
      return tafComponentsLookUp(
        {
          ...payload,
          initialProps: {
            ...payload.initialProps,
            ...(config?.GW_FEATURE_TAF_AUTOTAF_ENABLED !== undefined && {
              isAutoTafEnabled: config?.GW_FEATURE_TAF_AUTOTAF_ENABLED,
            }),
          },
        },
        {
          config: {
            baseURL: config?.GW_TAF_BASE_URL,
            appURL: config?.GW_APP_URL,
            authTokenURL: config?.GW_AUTH_TOKEN_URL,
            authClientId: config?.GW_AUTH_CLIENT_ID,
          },
          auth,
          onSetAuth,
        },
        ComponentsLookUpWrapper,
      );
    case 'CapWarningList':
    case 'CapWarningMap':
      return capComponentsLookup({
        ...payload,
        productConfigKey:
          config?.GW_CAP_CONFIGURATION_FILENAME ?? 'capWarningPresets.json',
      });
    case 'SigmetModule':
      return SigmetComponentsLookUp(
        payload,
        {
          config: {
            baseURL: config?.GW_SIGMET_BASE_URL,
            appURL: config?.GW_APP_URL,
            authTokenURL: config?.GW_AUTH_TOKEN_URL,
            authClientId: config?.GW_AUTH_CLIENT_ID,
          },
          auth,
          onSetAuth,
          productType: 'sigmet',
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentsLookUpWrapper as any,
      );
    case 'AirmetModule':
      return AirmetComponentsLookUp(
        payload,
        {
          config: {
            baseURL: config?.GW_AIRMET_BASE_URL,
            appURL: config?.GW_APP_URL,
            authTokenURL: config?.GW_AUTH_TOKEN_URL,
            authClientId: config?.GW_AUTH_CLIENT_ID,
          },
          auth,
          onSetAuth,
          productType: 'airmet',
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentsLookUpWrapper as any,
      );
    case 'WarningList':
    case 'MaritimeWarnings':
    case 'WarningsTimeline':
      return warningListComponentsLookUp(
        payload,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ComponentsLookUpWrapper as any,
      );
    case 'SpaceweatherModule':
      return spaceweatherComponentsLookUp(payload);
    case 'Soundings':
      return soundingsLookUp(payload)!;
    case 'TrajksModule':
      return trajksComponentsLookUp(payload);
    default:
      return coreComponentsLookUp({
        ...payload,
        initialProps: {
          ...payload.initialProps,
          mapControls: config?.GW_DRAWINGS_BASE_URL !== undefined && (
            <>
              <DrawingToolMapButtonConnect mapId={payload.id} />
              <ObjectManagerMapButtonConnect mapId={payload.id} />
            </>
          ),
          shouldDisplayDrawControls: config?.GW_DRAWINGS_BASE_URL !== undefined,
          displaySearchButtonInMap: config?.GW_FEATURE_DISPLAY_SEARCH_ON_MAP,
          displayDataExplorerButtonOnMap:
            config?.GW_DATA_EXPLORER_BUTTON_ON_MAP,
        },
      });
  }
};
